import type { LockablePagePermission } from "./pageCopy";

type PreviewStat = { label: string; value: string };
type PreviewRow = { title: string; meta: string; tag: string };

/**
 * Prévia estática de uma página, desenhada desfocada atrás do bloqueio. A
 * grade do protótipo é fixa: três números e quatro linhas.
 */
export type PagePreview = {
  title: string;
  sub: string;
  cta: string;
  stats: readonly [PreviewStat, PreviewStat, PreviewStat];
  rows: readonly [PreviewRow, PreviewRow, PreviewRow, PreviewRow];
};

/* Dados de exemplo de `previewData()` do protótipo (docs/design-system/
   Atendimento.dc.html), verbatim. É ilustração fixa, igual para todo cliente:
   nunca dado real da conta (a página real responderia 403). Chaves do
   protótipo → chave de página: ai → agent, followups → follow-ups,
   broadcasts → campaigns, team → team-management. O Playground usa a do
   Agente de IA porque o gate dele é a mesma chave. */
export const LOCKABLE_PAGE_PREVIEW: Record<
  LockablePagePermission,
  PagePreview
> = {
  "ui:contacts:view": {
    title: "Contatos",
    sub: "1.284 contatos · 3 listas",
    cta: "Novo contato",
    stats: [
      { label: "Contatos", value: "1.284" },
      { label: "Novos em 7 dias", value: "96" },
      { label: "Listas", value: "3" },
    ],
    rows: [
      {
        title: "Ana Beatriz Ramos",
        meta: "(11) 99872-3310 · Suporte Técnico",
        tag: "Cliente",
      },
      {
        title: "Carlos Pinheiro",
        meta: "(11) 98120-4472 · Comercial",
        tag: "Lead",
      },
      {
        title: "Débora Nunes",
        meta: "(21) 99311-8802 · Financeiro",
        tag: "Cliente",
      },
      {
        title: "Eduardo Lima",
        meta: "(31) 98444-1290 · Comercial",
        tag: "Lead",
      },
    ],
  },
  "ui:agent:view": {
    title: "Agente de IA",
    sub: "Atendimento automático · 2 fluxos ativos",
    cta: "Editar agente",
    stats: [
      { label: "Resolvidas pela IA", value: "68%" },
      { label: "Transferências", value: "142" },
      { label: "Tempo médio", value: "1m 12s" },
    ],
    rows: [
      {
        title: "Boas-vindas",
        meta: "Responde e identifica a área da conversa",
        tag: "Ativo",
      },
      {
        title: "Triagem de cobrança",
        meta: "Direciona para Financeiro",
        tag: "Ativo",
      },
      {
        title: "Pós-venda",
        meta: "Pesquisa de satisfação em 24h",
        tag: "Pausado",
      },
      {
        title: "Base de conhecimento",
        meta: "18 documentos indexados",
        tag: "Sincronizado",
      },
    ],
  },
  "ui:integrations:view": {
    title: "Integrações",
    sub: "Conecte o SuperAgents ao seu stack",
    cta: "Nova integração",
    stats: [
      { label: "Disponíveis", value: "24" },
      { label: "Conectadas", value: "0" },
      { label: "Webhooks", value: "0" },
    ],
    rows: [
      {
        title: "RD Station",
        meta: "Envia leads qualificados para o CRM",
        tag: "Conectar",
      },
      {
        title: "Planilhas Google",
        meta: "Exporta contatos e conversas",
        tag: "Conectar",
      },
      {
        title: "Webhook personalizado",
        meta: "Eventos de conversa em tempo real",
        tag: "Conectar",
      },
      {
        title: "Zapier",
        meta: "Mais de 5 mil automações",
        tag: "Conectar",
      },
    ],
  },
  "ui:follow-ups:view": {
    title: "Follow-ups",
    sub: "Retomadas automáticas de conversa",
    cta: "Novo follow-up",
    stats: [
      { label: "Sequências", value: "4" },
      { label: "Respostas retomadas", value: "312" },
      { label: "Taxa de resposta", value: "37%" },
    ],
    rows: [
      {
        title: "Sem resposta em 24h",
        meta: "1 mensagem · Comercial",
        tag: "Ativo",
      },
      {
        title: "Orçamento enviado",
        meta: "3 mensagens em 7 dias",
        tag: "Ativo",
      },
      {
        title: "Carrinho abandonado",
        meta: "2 mensagens em 48h",
        tag: "Pausado",
      },
      {
        title: "Reativação de base",
        meta: "1 mensagem · 90 dias",
        tag: "Rascunho",
      },
    ],
  },
  "ui:campaigns:view": {
    title: "Disparos",
    sub: "Campanhas em massa no WhatsApp",
    cta: "Novo disparo",
    stats: [
      { label: "Enviados no mês", value: "4.120" },
      { label: "Entregues", value: "97%" },
      { label: "Respostas", value: "418" },
    ],
    rows: [
      {
        title: "Promoção de setembro",
        meta: "Enviado para 1.200 contatos",
        tag: "Concluído",
      },
      {
        title: "Aviso de manutenção",
        meta: "Agendado para 18/09 09:00",
        tag: "Agendado",
      },
      {
        title: "Novos planos",
        meta: "Rascunho · 2 listas",
        tag: "Rascunho",
      },
      {
        title: "Pesquisa NPS",
        meta: "Enviado para 840 contatos",
        tag: "Concluído",
      },
    ],
  },
  "ui:team-management:view": {
    title: "Equipe",
    sub: "Pessoas, áreas e permissões",
    cta: "Convidar pessoa",
    stats: [
      { label: "Pessoas", value: "6" },
      { label: "Áreas", value: "3" },
      { label: "Convites pendentes", value: "1" },
    ],
    rows: [
      {
        title: "Marina Duarte",
        meta: "Suporte Técnico · Comercial",
        tag: "Admin",
      },
      { title: "Ricardo Alves", meta: "Suporte Técnico", tag: "Membro" },
      { title: "Júlia Campos", meta: "Financeiro", tag: "Membro" },
      {
        title: "pedro@fonteaguas.com.br",
        meta: "Convite enviado há 2 dias",
        tag: "Pendente",
      },
    ],
  },
  /* O `previewData()` do protótipo não tem Áreas: os nomes, as pessoas e a
     transferência pela IA vêm das áreas de exemplo dele (`seedAreas()`), com
     uma quarta área sem ninguém para fechar a grade de quatro linhas. */
  "ui:areas:view": {
    title: "Áreas",
    sub: "4 áreas · 4 pessoas",
    cta: "Nova área",
    stats: [
      { label: "Áreas", value: "4" },
      { label: "Pessoas em áreas", value: "4" },
      { label: "Com transferência da IA", value: "2" },
    ],
    rows: [
      { title: "Suporte Técnico", meta: "2 pessoas", tag: "IA transfere" },
      { title: "Comercial", meta: "3 pessoas", tag: "IA transfere" },
      { title: "Financeiro", meta: "2 pessoas", tag: "Só atendentes" },
      { title: "Ouvidoria", meta: "0 pessoas", tag: "Sem pessoas" },
    ],
  },
  "ui:dashboard:view": {
    title: "Dashboard",
    sub: "Desempenho do atendimento",
    cta: "Exportar",
    stats: [
      { label: "Conversas no mês", value: "2.938" },
      { label: "Tempo de 1ª resposta", value: "48s" },
      { label: "Encerradas pela IA", value: "61%" },
    ],
    rows: [
      {
        title: "Volume por área",
        meta: "Suporte 48% · Comercial 34% · Financeiro 18%",
        tag: "Mês",
      },
      {
        title: "Ranking de atendentes",
        meta: "Ricardo · Júlia · Marina",
        tag: "Mês",
      },
      {
        title: "Horários de pico",
        meta: "10h às 12h e 15h às 17h",
        tag: "Semana",
      },
      {
        title: "Motivos de encerramento",
        meta: "Resolvido 72% · Sem resposta 14%",
        tag: "Mês",
      },
    ],
  },
  "ui:settings:view": {
    title: "Configurações",
    sub: "Atendimentos e notificações da conta",
    cta: "Salvar",
    stats: [
      { label: "Regras ativas", value: "5" },
      { label: "WhatsApps", value: "2" },
      { label: "Automações", value: "3" },
    ],
    rows: [
      {
        title: "Distribuição de novas conversas",
        meta: "Por área, com fila automática",
        tag: "Ativo",
      },
      {
        title: "Arquivamento automático",
        meta: "Após 48h sem resposta",
        tag: "Ativo",
      },
      {
        title: "Conversas encerradas",
        meta: "Voltam para a IA",
        tag: "Ativo",
      },
      { title: "Sons de notificação", meta: "Por usuário", tag: "Ativo" },
    ],
  },
};
